import { create } from "zustand";

const THEME_KEY = "app_theme";
const PALETTE_KEY = "app_palette";

/*
 * Brand gradient: `primary` is the main colour, `deep` the dark end of the
 * gradients. Every tinted token in theme.css is derived from these two.
 */
export const PALETTE_PRESETS = [
  { id: "red", name: "Red", primary: "#e3262c", deep: "#9a1016" },
  { id: "crimson", name: "Crimson", primary: "#c2185b", deep: "#6d0a32" },
  { id: "rose", name: "Rose", primary: "#e84a7f", deep: "#8e1c47" },
  { id: "magenta", name: "Magenta", primary: "#d946ef", deep: "#7a1587" },
  { id: "violet", name: "Violet", primary: "#a855f7", deep: "#5b1a8f" },
  { id: "purple", name: "Purple", primary: "#8b3dff", deep: "#4b1597" },
  { id: "indigo", name: "Indigo", primary: "#4f46e5", deep: "#27208a" },
  { id: "blue", name: "Blue", primary: "#2f6fed", deep: "#123f9a" },
  { id: "sky", name: "Sky", primary: "#0ea5e9", deep: "#075985" },
  { id: "teal", name: "Teal", primary: "#0fa3a3", deep: "#085c5e" },
  { id: "emerald", name: "Emerald", primary: "#10b981", deep: "#065f46" },
  { id: "green", name: "Green", primary: "#16a461", deep: "#0a5c37" },
  { id: "lime", name: "Lime", primary: "#7cb518", deep: "#3f5e0a" },
  { id: "gold", name: "Gold", primary: "#c9a227", deep: "#6b5410" },
  { id: "amber", name: "Amber", primary: "#f0a018", deep: "#8a5200" },
  { id: "orange", name: "Orange", primary: "#f2711c", deep: "#9a3b08" },
  { id: "brown", name: "Brown", primary: "#a0522d", deep: "#5a2b12" },
  { id: "slate", name: "Slate", primary: "#64748b", deep: "#1e293b" },
];

export const DEFAULT_PALETTE = {
  primary: PALETTE_PRESETS[0].primary,
  deep: PALETTE_PRESETS[0].deep,
};

export const HEX_RE = /^#[0-9a-f]{6}$/i;

const readStoredTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch (e) {
    // storage unavailable
  }
  return "dark";
};

const readStoredPalette = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(PALETTE_KEY));
    if (HEX_RE.test(stored?.primary) && HEX_RE.test(stored?.deep)) {
      return { primary: stored.primary, deep: stored.deep, end: HEX_RE.test(stored.end) ? stored.end : stored.primary };
    }
  } catch (e) {
    // storage unavailable or bad value
  }
  return DEFAULT_PALETTE;
};

const hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l * 100];

  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [(h * 60 + 360) % 360, s * 100, l * 100];
};

const hslToHex = (h, s, l) => {
  const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = l / 100 - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

// brighter, more saturated version of the primary (highlights, ring end)
const strongOf = (hex) => {
  const [h, s, l] = hexToHsl(hex);
  if (s < 5) return hex; // greys stay grey
  return hslToHex(h, Math.min(100, s + 23), Math.min(70, l + 6));
};

// `weight` of colour `a` mixed into colour `b`, like CSS color-mix in srgb
const mixHex = (a, b, weight) => {
  const channel = (hex, i) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  return `#${[0, 1, 2]
    .map((i) =>
      Math.round(channel(a, i) * weight + channel(b, i) * (1 - weight))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
};

/*
 * Colour of the top edge of the mobile header (centre of the screen), so
 * the Android status bar blends into it. Mirrors theme.css / WaveBackground:
 * --wave-base with the wave shading and band faded in at
 * --wave-fade-in-opacity.
 */
const WAVE_BASE = "#121111";
const topBarColorOf = (primary, deep) => {
  const waveRed = mixHex(deep, WAVE_BASE, 0.45);
  const shaded = mixHex(waveRed, WAVE_BASE, 0.2);
  const band = mixHex(waveRed, strongOf(primary), 0.75);
  return mixHex(band, shaded, 0.04);
};

// darker version of a colour, used as the gradient shade
export const shadeOf = (hex) => {
  const [h, s, l] = hexToHsl(hex);
  return hslToHex(h, s, l * 0.58);
};

const applyTheme = (theme) => {
  document.documentElement.setAttribute("data-theme", theme);
};

const applyPalette = ({ primary, deep, end }) => {
  const style = document.documentElement.style;
  style.setProperty("--brand", primary);
  style.setProperty("--brand-strong", strongOf(primary));
  style.setProperty("--brand-deep", deep);
  style.setProperty("--brand-gradient-end", end || primary);

  // The phone's top bar can't read CSS variables, so mirror the header's
  // top colour into the theme-color meta tag.
  let themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement("meta");
    themeColorMeta.name = "theme-color";
    document.head.appendChild(themeColorMeta);
  }
  themeColorMeta.setAttribute("content", topBarColorOf(primary, deep));
};

const initialTheme = readStoredTheme();
const initialPalette = readStoredPalette();
applyTheme(initialTheme);
applyPalette(initialPalette);

export const useThemeStore = create((set, get) => ({
  theme: initialTheme,
  palette: initialPalette,
  companyThemes: {},
  companyThemeRevision: 0,

  setCompanyThemes: (themes) => {
    const companyThemes = Object.fromEntries(themes.map(item => [item.theme, {
      primary: item.primary_color,
      deep: item.gradient_start,
      end: item.gradient_end,
    }]));
    set({ companyThemes, companyThemeRevision: get().companyThemeRevision + 1 });
    get().setPalette(companyThemes[get().theme] || { ...DEFAULT_PALETTE, end: DEFAULT_PALETTE.primary });
  },

  setTheme: (theme) => {
    if (!["light", "dark"].includes(theme)) return;
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch (e) {
      // storage unavailable
    }
    set({ theme });
    if (Object.keys(get().companyThemes).length) {
      get().setPalette(get().companyThemes[theme] || { ...DEFAULT_PALETTE, end: DEFAULT_PALETTE.primary });
    }
  },

  toggleTheme: () => get().setTheme(get().theme === "dark" ? "light" : "dark"),

  setPalette: (changes) => {
    const palette = { ...get().palette, ...changes };
    if (changes.primary && !changes.end) palette.end = changes.primary;
    if (!HEX_RE.test(palette.primary) || !HEX_RE.test(palette.deep) ||
        (palette.end && !HEX_RE.test(palette.end))) return;

    applyPalette(palette);
    try {
      localStorage.setItem(PALETTE_KEY, JSON.stringify(palette));
    } catch (e) {
      // storage unavailable
    }
    set({ palette });
  },

  resetPalette: () => get().setPalette(DEFAULT_PALETTE),
}));
